import { Box, Container, makeStyles, Theme, withWidth } from '@material-ui/core';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import { useDarkMode, useParallax } from 'hooks';
import { parallax1, parallax2, parallax3, parallax4, parallax5, parallax6, parallax7 } from 'images/company';
import { parallaxGalleryBg } from 'images/inline-svg';
import { FC } from 'react';
import { animated } from 'react-spring';

type StyleProps = Partial<{ width: Breakpoint; isDarkMode: boolean }>;

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    height: '170vh',
    position: 'relative',
  },
  wrapper: {
    backgroundColor: ({ isDarkMode }: StyleProps) => (isDarkMode ? theme.palette.background.paper : theme.palette.info.light),
    overflow: 'hidden',
    backgroundImage: parallaxGalleryBg,
    backgroundRepeat: 'no-repeat',
    backgroundSize: ({ width }: StyleProps) => (width === 'xs' ? 'cover' : 'contain'),
    marginTop: theme.spacing(11),
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(20),
    },
  },
  image: {
    boxShadow: '0px 4px 66px 10px rgba(0, 0, 0, 0.25)',
    maxWidth: '100%',
  },
}));

type TWidthBreakpoint = { [key in Breakpoint]: number };

interface IParallaxItems {
  image: string;
  transformItem: number;
  responsive: TWidthBreakpoint;
  top?: string | number;
  left?: string | number;
  right?: string | number;
  bottom?: string | number;
}

const parallaxItems: IParallaxItems[] = [
  {
    image: parallax1,
    transformItem: -0.2,
    top: '30%',
    left: '10%',
    responsive: {
      xl: 100,
      lg: 100,
      md: 45,
      sm: 55,
      xs: 60,
    },
  },
  {
    image: parallax2,
    transformItem: -0.15,
    right: '10%',
    top: '50%',
    responsive: {
      xl: 100,
      lg: 100,
      md: 35,
      sm: 45,
      xs: 55,
    },
  },
  {
    image: parallax3,
    transformItem: -0.1,
    bottom: '5%',
    left: '0%',
    responsive: {
      xl: 100,
      lg: 100,
      md: 40,
      sm: 60,
      xs: 55,
    },
  },
  {
    image: parallax4,
    transformItem: 0.05,
    right: '5%',
    bottom: '20%',
    responsive: {
      xl: 100,
      lg: 100,
      md: 25,
      sm: 35,
      xs: 35,
    },
  },
  {
    image: parallax5,
    transformItem: 0.2,
    top: '10%',
    right: '0%',
    responsive: {
      xl: 100,
      lg: 100,
      md: 30,
      sm: 40,
      xs: 50,
    },
  },
  {
    image: parallax6,
    transformItem: 0.05,
    top: '10%',
    left: '0%',
    responsive: {
      xl: 100,
      lg: 100,
      md: 100,
      sm: 40,
      xs: 50,
    },
  },
  {
    image: parallax7,
    transformItem: 0.02,
    top: '55%',
    right: '35%',
    responsive: {
      xl: 100,
      lg: 100,
      md: 100,
      sm: 35,
      xs: 65,
    },
  },
];

const ParallaxGallery: FC<{ width: Breakpoint }> = ({ width }) => {
  const { isDarkMode } = useDarkMode();
  const classes = useStyles({ width, isDarkMode });
  const { ParallaxProvider, transform } = useParallax();

  return (
    <Box className={classes.wrapper}>
      <Container fixed className={classes.container}>
        <ParallaxProvider>
          {parallaxItems.map(
            (
              { image, transformItem, responsive, top = 'auto', left = 'auto', right = 'auto', bottom = 'auto' },
              index,
              origin,
            ) => (
              <animated.img
                key={image}
                className={classes.image}
                style={{
                  position: 'absolute',
                  transform: transform(transformItem),
                  zIndex: origin.length - index,
                  maxWidth: responsive[width] + '%',
                  top,
                  left,
                  right,
                  bottom,
                }}
                src={image}
              />
            ),
          )}
        </ParallaxProvider>
      </Container>
    </Box>
  );
};

export default withWidth()(ParallaxGallery);
